import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * The PDP Hero v2 media column.
 *
 * Which media a colour shows is decided in Liquid (see `_pdp-media-grid-v2`), so this
 * component's only job is to adopt the freshly filtered grid out of the Section
 * Rendering API response that the variant picker has already fetched. No request of
 * its own, and no client-side copy of the alt-text matching rule.
 *
 * @extends {Component}
 */
class PdpMediaGrid extends Component {
  /** @type {EventTarget | null} */
  #eventSource = null;

  connectedCallback() {
    super.connectedCallback();

    // The picker lives in the other column of the same section, and VariantUpdateEvent
    // bubbles, so the section is the nearest node that sees both.
    this.#eventSource = this.closest('.shopify-section') ?? document;
    this.#eventSource.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventSource?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { productId, html } = event.detail.data;

    // Combined listings can swap the product out from under us; only take markup that
    // belongs to the product this grid is rendering.
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const source = html?.querySelector('pdp-media-grid');
    if (!source) return;

    morph(this, source, MORPH_OPTIONS);

    // Morphing changed the slide set, so the carousel's slide count, dot window and
    // scroll position are all stale.
    const carousel = this.querySelector('pdp-media-carousel');
    if (carousel instanceof HTMLElement && 'refresh' in carousel && typeof carousel.refresh === 'function') {
      carousel.refresh();
    }
  };
}

if (!customElements.get('pdp-media-grid')) {
  customElements.define('pdp-media-grid', PdpMediaGrid);
}
